// src/components/EmployeeDetail.js
//
// Read-only employee detail screen for matrix personas with `emp:view`
// but not `emp:edit` — they can see employees in the All Employees list
// but until this screen existed, clicking through went nowhere (the
// edit route gates on emp:edit and silently no-ops).
//
// Layout: tabbed sections matching the wizard's 4-step structure.
// Sensitive Info tab is conditionally rendered based on emp:pii:view.
//
// Edit button at top-right routes to the edit screen for users who also
// have emp:edit. Otherwise the screen is purely read-only.
//
// PRD_UAM_CustomRoles2.md §6.2.

import { useEffect, useState } from "react";
import Icon from "./icons/Icon";
import { useNavigate, useParams } from "react-router-dom";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { isEmployeeActiveToday } from "../utils/employeeFilters";
import { toYMDString } from "../utils/periodDate";
import { db } from "../firebase/config";
import { useAuth } from "../hooks/useAuth";
import ResetKioskPinModal from "./settings/ResetKioskPinModal";
import ContributionBasisNote from "./ContributionBasisNote";
import { useEmployee } from "../hooks/useEmployee";

// ============================================================
// Formatters
// ============================================================

// 'YYYY-MM-DD' — the app-wide date standard (DESIGN-STANDARDS.md §13).
// toYMDString reads LOCAL Y/M/D components, never toISOString, so a Manila
// timestamp cannot render the previous day.
// Was an identical copy of the helper in employee-steps/StepReview.js.
const fmtDate = (value) => toYMDString(value) || "—";

const fmtMoney = (value) => {
  const num = parseFloat(value) || 0;
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(num);
};

const fmtWorkingDays = (workSchedule) => {
  if (!workSchedule) return "—";
  const order = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const days = order
    .map((day, idx) => (workSchedule[day]?.isWorkingDay ? labels[idx] : null))
    .filter(Boolean);
  return days.length ? days.join(", ") : "—";
};

// ============================================================
// Display primitives
// ============================================================

const DetailRow = ({ label, value, mono }) => (
  <div
    style={{
      display: "flex",
      gap: "12px",
      padding: "8px 0",
      borderBottom: "1px solid var(--ui-surface-recessed)",
      fontSize: "0.875rem",
    }}
  >
    <div style={{ color: "var(--ui-text-muted)", minWidth: "180px" }}>{label}</div>
    <div
      style={{
        color: "var(--ui-text)",
        fontWeight: 500,
        fontVariantNumeric: mono ? "tabular-nums" : "normal",
        fontSize: mono ? "0.8125rem" : "0.875rem",
      }}
    >
      {value || "—"}
    </div>
  </div>
);

// ============================================================
// Tab content renderers
// ============================================================

const PersonalInfoTab = ({ employee }) => {
  const personal = employee.personalInfo || {};
  const fullName = `${personal.firstName || ""} ${personal.middleName || ""} ${personal.lastName || ""}`
    .replace(/\s+/g, " ")
    .trim();

  return (
    <>
      <DetailRow label="Employee ID" value={employee.employeeId} mono />
      <DetailRow label="Name" value={fullName} />
      <DetailRow label="Email" value={personal.email} />
      <DetailRow label="Mobile" value={personal.mobile} />
      <DetailRow label="Date of Birth" value={personal.dateOfBirth} />
      <DetailRow
        label="Gender"
        value={personal.gender ? personal.gender.charAt(0).toUpperCase() + personal.gender.slice(1) : "—"}
      />
      <DetailRow
        label="Marital Status"
        value={personal.maritalStatus ? personal.maritalStatus.charAt(0).toUpperCase() + personal.maritalStatus.slice(1) : "—"}
      />
      <DetailRow
        label="Status"
        value={
          <span
            style={{
              padding: "2px 10px",
              borderRadius: "12px",
              fontSize: "0.75rem",
              fontWeight: 600,
              background: isEmployeeActiveToday(employee) ? "var(--ui-success-tint)" : "var(--ui-warning-tint)",
              color: isEmployeeActiveToday(employee) ? "var(--ui-success-text)" : "var(--ui-warning-text)",
            }}
          >
            {isEmployeeActiveToday(employee) ? "active" : "inactive"}
          </span>
        }
      />
    </>
  );
};

// H11: `basisAgencies` are the agencies this company composes from actual pay.
// Each row here is its own agency, so — unlike StepReview's single combined row
// — the note sits under the row it is about, matching the wizard.
const EmploymentTab = ({ employee, basisAgencies = [] }) => {
  const employment = employee.employment || {};
  return (
    <>
      <DetailRow label="Department" value={employment.department} />
      <DetailRow label="Employment Type" value={employment.employmentType} />
      <DetailRow label="Start Date" value={fmtDate(employment.startDate)} />
      <DetailRow label="End Date" value={fmtDate(employment.endDate)} />
      <DetailRow label="Working Days" value={fmtWorkingDays(employment.workSchedule)} />
      <DetailRow
        label="Schedule"
        value={
          employment.workSchedule?.monday
            ? `${employment.workSchedule.monday.startTime} – ${employment.workSchedule.monday.endTime} (${employment.workSchedule.monday.breakDuration}min break)`
            : "—"
        }
      />
      <div style={{ marginTop: "16px", padding: "12px", background: "var(--ui-bg)", borderRadius: "8px" }}>
        <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--ui-primary-hover)", marginBottom: "8px", letterSpacing: "0.5px" }}>
          GOVERNMENT CONTRIBUTIONS
        </div>
        <DetailRow label="SSS" value={fmtMoney(employment.sssContribution)} mono />
        {basisAgencies.includes("SSS") && <ContributionBasisNote agencies={["SSS"]} />}
        <DetailRow label="PhilHealth" value={fmtMoney(employment.philhealthContribution)} mono />
        {basisAgencies.includes("PhilHealth") && (
          <ContributionBasisNote agencies={["PhilHealth"]} />
        )}
        <DetailRow label="Pag-IBIG" value={fmtMoney(employment.pagibigContribution)} mono />
        <DetailRow label="Withholding Tax" value={fmtMoney(employment.withholdingTax)} mono />
      </div>
    </>
  );
};

const SensitiveInfoTab = ({ employee }) => {
  const employment = employee.employment || {};
  const banking = employee.banking || {};
  const govIds = employee.governmentIds || {};

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          padding: "0.75rem 1rem",
          marginBottom: "1rem",
          background: "var(--ui-accent-tint)",
          border: "1px solid var(--ui-accent)",
          borderRadius: "8px",
        }}
      >
        <span style={{ fontSize: "1.25rem" }}><Icon name="lock" size={18} /></span>
        <div style={{ fontWeight: 600, color: "var(--ui-warning-text)", fontSize: "0.875rem" }}>
          Sensitive Info — PII protected
        </div>
      </div>

      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--ui-primary-hover)", marginBottom: "4px", letterSpacing: "0.5px" }}>
          COMPENSATION
        </div>
        <DetailRow label="Salary Type" value={employment.salaryType ? employment.salaryType.charAt(0).toUpperCase() + employment.salaryType.slice(1) : "—"} />
        <DetailRow label="Daily Salary" value={fmtMoney(employment.dailySalary)} mono />
        <DetailRow label="Monthly Salary" value={fmtMoney(employment.monthlySalary)} mono />
      </div>

      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--ui-primary-hover)", marginBottom: "4px", letterSpacing: "0.5px" }}>
          BANK DETAILS
        </div>
        <DetailRow label="Account Name" value={banking.accountName} />
        <DetailRow label="Account Number" value={banking.accountNumber} mono />
      </div>

      <div>
        <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--ui-primary-hover)", marginBottom: "4px", letterSpacing: "0.5px" }}>
          GOVERNMENT IDS
        </div>
        <DetailRow label="SSS" value={govIds.sss} mono />
        <DetailRow label="PhilHealth" value={govIds.philhealth} mono />
        <DetailRow label="Pag-IBIG" value={govIds.pagibig} mono />
        <DetailRow label="TIN" value={govIds.tin} mono />
      </div>
    </>
  );
};

// ============================================================
// Main component
// ============================================================

export const EmployeeDetail = () => {
  const navigate = useNavigate();
  const { slug, employeeId: urlEmployeeId } = useParams();
  const { user, company, hasPermission } = useAuth();

  const canEdit = hasPermission("emp:edit");
  const canViewPii = hasPermission("emp:pii:view");
  const [showPinModal, setShowPinModal] = useState(false);

  // Resolve docId from the URL's user-facing employeeId. The list view
  // navigates here using the 4-digit employeeId, but useEmployee + the
  // service layer all key on the Firestore docId. Same pattern as
  // AddEmployee.js's loadEmployeeData.
  const [docId, setDocId] = useState(null);
  const [resolveError, setResolveError] = useState(null);
  // PRD_PhilHealthContributionBasis H11. This screen has no settings read of
  // its own, so this is a NEW one — rules already allow any company member to
  // read settings/payrollRates. Fails quiet: on error or while pending the list
  // stays empty and no note renders, because a missing note is better than a
  // wrong one.
  const [basisAgencies, setBasisAgencies] = useState([]);

  useEffect(() => {
    if (!company?.id) return undefined;
    let cancelled = false;
    getDoc(doc(db, "companies", company.id, "settings", "payrollRates"))
      .then((snap) => {
        if (cancelled) return;
        const rates = snap.exists() ? snap.data() : {};
        setBasisAgencies(
          [
            rates.sssBasis?.enabled === true && "SSS",
            rates.philhealthBasis?.enabled === true && "PhilHealth",
          ].filter(Boolean)
        );
      })
      .catch(() => { /* no note — see above */ });
    return () => { cancelled = true; };
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id || !urlEmployeeId) return;

    const employeesRef = collection(db, "companies", company.id, "employees");
    const q = query(
      employeesRef,
      where("role", "==", "employee"),
      where("employeeId", "==", urlEmployeeId),
    );

    getDocs(q)
      .then((snap) => {
        if (snap.empty) {
          setResolveError("Employee not found");
          return;
        }
        setDocId(snap.docs[0].id);
      })
      .catch((err) => {
        console.error("[EmployeeDetail] resolve docId:", err);
        setResolveError(err.message || "Failed to resolve employee");
      });
  }, [company?.id, urlEmployeeId]);

  const { employee, hasPii, loading, error } = useEmployee(company?.id, docId);

  const [activeTab, setActiveTab] = useState("personal");

  // Build tabs dynamically — Sensitive Info hidden for callers without emp:pii:view
  const tabs = [
    { id: "personal", label: "Personal Info" },
    { id: "employment", label: "Employment" },
    ...(canViewPii ? [{ id: "sensitive", label: "Sensitive Info" }] : []),
  ];

  if (!user || !company) return null;

  if (resolveError || error) {
    return (
      <div className="add-employee-container">
        <div style={{ padding: "2rem", textAlign: "center", color: "var(--ui-danger)" }}>
          {resolveError || error.message || error}
        </div>
        <button
          onClick={() => navigate(`/company/${slug}/employees`)}
          className="btn btn--secondary"
        >
          <Icon name="arrow-left" size={14} aria-hidden="true" /> Back to All Employees
        </button>
      </div>
    );
  }

  if (loading || !employee) {
    return (
      <div className="employee-loading">
        <div className="employee-spinner"></div>
      </div>
    );
  }

  const personal = employee.personalInfo || {};
  const fullName = `${personal.firstName || ""} ${personal.middleName || ""} ${personal.lastName || ""}`
    .replace(/\s+/g, " ")
    .trim();

  return (
    <div className="add-employee-container">
      {/* Top bar — back link + page title + edit action */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1rem",
        }}
      >
        <button
          onClick={() => navigate(`/company/${slug}/employees`)}
          style={{
            background: "none",
            border: "none",
            color: "var(--ui-primary)",
            cursor: "pointer",
            fontSize: "0.875rem",
            padding: "4px 0",
          }}
        >
          <Icon name="arrow-left" size={14} aria-hidden="true" /> Back to All Employees
        </button>

        {canEdit && (
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => setShowPinModal(true)}
              className="btn btn--secondary"
              style={{ padding: "8px 16px" }}
            >
              Reset Kiosk PIN
            </button>
            <button
              onClick={() =>
                navigate(`/company/${slug}/employees/${urlEmployeeId}/edit`)
              }
              className="btn btn--primary"
              style={{ padding: "8px 16px" }}
            >
              <Icon name="pencil" size={14} aria-hidden="true" /> Edit Employee
            </button>
          </div>
        )}
      </div>

      {showPinModal && (
        <ResetKioskPinModal
          employeeId={employee.employeeId}
          employeeName={fullName}
          onClose={() => setShowPinModal(false)}
        />
      )}

      {/* Page title — name + employeeId */}
      <h1
        style={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: "var(--ui-primary)",
          margin: "0 0 4px 0",
        }}
      >
        {fullName}
      </h1>
      <div
        style={{
          fontVariantNumeric: "tabular-nums",
          fontSize: "0.875rem",
          color: "var(--ui-text-muted)",
          marginBottom: "1.5rem",
        }}
      >
        ID: {employee.employeeId}
      </div>

      {/* PII incomplete banner — visible to all viewers regardless of PII access */}
      {hasPii === false && (
        <div
          style={{
            background: "var(--ui-warning-tint)",
            border: "1px solid var(--ui-warning)",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "1.5rem",
            display: "flex",
            gap: "12px",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "var(--ui-warning)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontWeight: 700,
              fontSize: "0.875rem",
            }}
          >
            !
          </div>
          <div style={{ color: "var(--ui-warning-text)", fontSize: "0.8125rem", lineHeight: 1.5 }}>
            <strong>Payroll info incomplete.</strong> This employee won't be
            included in payroll runs until salary, banking, and government IDs
            are added by a user with Sensitive Info access.
          </div>
        </div>
      )}

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: "2px",
          borderBottom: "2px solid var(--ui-border)",
          marginBottom: "1.5rem",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 18px",
              background: "none",
              border: "none",
              borderBottom: activeTab === tab.id ? "2px solid var(--ui-primary)" : "2px solid transparent",
              marginBottom: "-2px",
              fontSize: "0.875rem",
              fontWeight: activeTab === tab.id ? 600 : 500,
              color: activeTab === tab.id ? "var(--ui-primary)" : "var(--ui-text-muted)",
              cursor: "pointer",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div
        style={{
          background: "white",
          padding: "1.5rem",
          borderRadius: "12px",
          border: "1px solid var(--ui-border)",
        }}
      >
        {activeTab === "personal" && <PersonalInfoTab employee={employee} />}
        {activeTab === "employment" && (
          <EmploymentTab employee={employee} basisAgencies={basisAgencies} />
        )}
        {activeTab === "sensitive" && canViewPii && (
          <SensitiveInfoTab employee={employee} />
        )}
      </div>
    </div>
  );
};
